import React, { FC, useRef, useState } from "react";
import { ReactComponent as ThreeDotsIcon } from "images/ThreeDots.svg";
import { ReactComponent as DownloadIcon } from "images/downloadArrowOnCircleFilled.svg";
import { ReactComponent as UploadIcon } from "images/UploadIcon.svg";
import { ReactComponent as RefreshIcon } from "images/Refresh.svg";
import { ReactComponent as HistoryIcon } from "images/History.svg";
import { ReactComponent as EditIcon } from "images/EditorIcon.svg";
import { ReactComponent as Flag } from "images/Flag.svg";
import { ReactComponent as TrashCanIcon } from "images/TrashCan.svg";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import { DocumentVersions } from "../../DocumentVersions/DocumentVersions";
import { ModalWindow } from "components/ModalWindow/ModalWindow";
import { Link, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { ChangeVersionMode } from "toolkit/documents/folders/folders.slice";
import { connectNames, dowloadDocument } from "helpers";
import {
	deleteDocument,
	getVersionList,
	restoreDocument,
	updateDocument,
	updateDocumentVersion,
} from "toolkit/documents/folders/folders.thunks";
import { IDocument } from "toolkit/documents/folders/types";
import { DeleteWindow } from "../../DeleteWindow/DeleteWindow";
import { AddingFolder } from "../../AddingFolder/AddingFolder";
import styles from "./Document.module.sass";
import cn from "classnames";

interface IModalWindow {
	content: React.ReactElement | null;
	visibility: boolean;
	width: string;
}

interface DocumentProps {
	document: IDocument;
	targetId?: number;
	flag?: boolean;
}

export const Document: FC<DocumentProps> = ({ document, targetId, flag }) => {
	const [menu, setMenu] = useState<boolean>(false);
	const menuRef = useRef(null);
	const { condition } = useParams();
	const { documentOrganization } = useAppSelector((state) => state.documents);
	const dispatch = useAppDispatch();
	const { versionMode } = useAppSelector((state) => state.folders);
	const [modalWindow, setModalWindow] = useState<IModalWindow>({
		content: null,
		visibility: false,
		width: "",
	});

	useOutsideAlerter(menuRef.current, () => setMenu(false));

	const openModalWindow = (component: React.ReactElement, width: string) => {
		setModalWindow({ ...modalWindow, content: component, visibility: true, width });
		setMenu(false);
	};

	const closeModalWindow = () => {
		setModalWindow({ ...modalWindow, content: null, visibility: false, width: "default" });
	};

	const openVersions = () => {
		dispatch(ChangeVersionMode(true));
		openModalWindow(<DocumentVersions targetId={document.id} close={closeModalWindow} />, "biggest");
		dispatch(getVersionList({ docId: document.id, archived: condition === "archive" }));
	};

	const updateDocumentHandler = (files: FileList | null) => {
		dispatch(updateDocument({ docId: document.id, file: files![0] }));
		setMenu(false);
	};

	const downloadHandler = () => {
		dowloadDocument(`/organization/folder/doc/${document.id}`, document.name, document.extension);
		setMenu(false);
	};

	const showDateAndTime = (): string => {
		const date = new Date(document.uploadDate);
		const different = date.getTimezoneOffset();
		if (different < 0) date.setMinutes(date.getMinutes() + Math.abs(different));
		else date.setMinutes(date.getMinutes() - Math.abs(different));
		return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
	};

	const updateVersionHandler = () => {
		setMenu(false);
		if (document.default) return;
		dispatch(updateDocumentVersion({ targetId: Number(targetId), docId: document.id }));
	};

	const openMenu = () => {
		if (menu) return;
		setTimeout(() => {
			setMenu((prev) => !prev);
		}, 1);
	};

	const restoreDocumentHandler = () => {
		dispatch(restoreDocument({ documentId: document.id }));
		setMenu(false);
	};

	const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		dispatch(deleteDocument({ documentId: document.id }));
		closeModalWindow();
	};

	return (
		<tr className={styles.row}>
			{modalWindow.visibility && (
				<ModalWindow close={closeModalWindow} width={modalWindow.width} padding={modalWindow.width !== "biggest"}>
					{modalWindow.content}
				</ModalWindow>
			)}
			<td className={styles.cell}>
				<div>
					{flag && document.default && (
						<div>
							<Flag className={styles.green} width="16" height="17" />
						</div>
					)}
					<span onClick={downloadHandler} className={styles.name}>
						{document.name}.{document.extension}
					</span>
				</div>
			</td>
			<td className={styles.cell}>
				<Link to={`/user/${document.responsible.id}`} className="link">
					{connectNames(document.responsible)}
				</Link>
			</td>
			<td className={styles.cell}>
				{showDateAndTime()}
				<button onClick={openMenu} className={styles["menu-btn"]}>
					<ThreeDotsIcon />
				</button>
				<div ref={menuRef} className={styles["menu-wrp"]}>
					{menu && (
						<div onClick={(e) => e.stopPropagation()} className={cn(styles.menu, versionMode && styles.width)}>
							<div onClick={downloadHandler} className={styles.btn}>
								<div className={styles["icon-wrp"]}>
									<DownloadIcon />
								</div>
								<span className={styles["btn-txt"]}>Скачать</span>
							</div>
							{versionMode ? (
								condition === "current" &&
								!documentOrganization?.archived && (
									<div onClick={updateVersionHandler} className={styles.btn}>
										<div className={styles["icon-wrp"]}>
											<Flag className={styles.green} />
										</div>
										<span className={styles["btn-txt"]}>Сделать актуальным</span>
									</div>
								)
							) : (
								<>
									{condition === "current" && !documentOrganization?.archived && (
										<label htmlFor="updateDoc" className={styles.btn}>
											<input
												onChange={(e) => updateDocumentHandler(e.target.files)}
												type="file"
												id="updateDoc"
												className="none"
												accept=".docx, .pdf, .rtf"
											/>
											<div className={styles["icon-wrp"]}>
												<UploadIcon className={styles.green} />
											</div>
											<span className={styles["btn-txt"]}>Загрузить новую версию</span>
										</label>
									)}
									<div onClick={openVersions} className={styles.btn}>
										<div className={styles["icon-wrp"]}>
											<HistoryIcon />
										</div>
										<span className={styles["btn-txt"]}>Версии док-та</span>
									</div>
									{!documentOrganization?.archived &&
										(condition === "current" ? (
											<>
												<div
													onClick={() =>
														openModalWindow(
															<AddingFolder
																title="Редактирование документа"
																document={document}
																close={closeModalWindow}
															/>,
															"",
														)
													}
													className={styles.btn}>
													<div className={styles["icon-wrp"]}>
														<EditIcon className={styles.red} />
													</div>
													<span className={styles["btn-txt"]}>Редактировать</span>
												</div>
												<div
													onClick={() =>
														openModalWindow(
															<DeleteWindow
																subtitle="Документ переместится в архив"
																onSubmit={(e) => submitHandler(e)}
																close={closeModalWindow}
															/>,
															"",
														)
													}
													className={styles.btn}>
													<div className={styles["icon-wrp"]}>
														<TrashCanIcon className={styles.red} />
													</div>
													<span className={styles["btn-txt"]}>В архив</span>
												</div>
											</>
										) : (
											<div onClick={restoreDocumentHandler} className={styles.btn}>
												<div className={styles["icon-wrp"]}>
													<RefreshIcon className={cn(styles.green, styles.refresh)} />
												</div>
												<span className={styles["btn-txt"]}>Восстановить</span>
											</div>
										))}
								</>
							)}
						</div>
					)}
				</div>
			</td>
		</tr>
	);
};
